import {FC, useCallback, useEffect, useState} from 'react';
import {YClientsDataType} from 'api/types/LeadForm';
import useAnalytics from 'utils/hooks/useAnalytics';
import {useYClientsNavigation} from 'utils/hooks/useNavigation';
import {FIXED_SCREENS, YClientsForm} from '@target-web/leadads-form-view';
import {bridge} from 'utils/bridge';
import {useYClientsFormController} from 'utils/hooks/useYClientsFormController';
import {useMasters} from 'utils/hooks/useMasters';
import {useServices} from 'utils/hooks/useServices';
import {useDates} from 'utils/hooks/useDates';
import {useMaster} from 'utils/hooks/useMaster';
import {useActivities} from 'utils/hooks/useActivities';
import {useFilialInfo} from 'utils/hooks/useFilialInfo';
import {useQuickWrite} from 'utils/hooks/useQuickWrite';
import {AppProps} from './types';
import {Spinner} from '@vkontakte/vkui';
import {useSearchParams} from 'utils/searchParamsContext';
import {fetchServices} from 'utils/requests';

type AppYClientsWrapperProps = AppProps & {
    formData: YClientsDataType;
};

type AppYClientsProps = AppYClientsWrapperProps & {
    initialServiceId: number | null;
};

const AppYClients: FC<AppYClientsProps> = ({formData, layout, initialServiceId, isShowCloseBtn}) => {
    const {clickToCallAnalyticEvent, clickToExternalLinkAnalyticEvent} = useAnalytics();

    const {history, isBack, goBack, goToPage, goToFirstPage, closeForm, onTransition, goToSpecific} =
        useYClientsNavigation({
            formData,
            initialServiceId,
        });

    const formController = useYClientsFormController({
        leadForm: formData,
        currentPage: history[history.length - 1],
        goToPage,
        initialServiceId,
        hasDateTimeOnPrevious: history.includes(FIXED_SCREENS.YCLIENTS_DATETIME),
    });

    const mastersState = useMasters({
        active: history[history.length - 1] === FIXED_SCREENS.YCLIENTS_MASTER,
        datetime: formController.formState.datetime,
        selectedStaffId: formController.formState.staffId,
        service_ids: formController.formState.services,
        needDateTime: !history.includes(FIXED_SCREENS.YCLIENTS_DATETIME) && formData.data.form.sequence_type === 'menu', // если предыдущим экраном была не дата и тип формы - меню
        resetStaffId: () => {
            formController.setStaffId(null);
        },
        resetDateTime: formController.resetDateTime,
    });
    const servicesState = useServices({
        active:
            history[history.length - 1] === FIXED_SCREENS.YCLIENTS_SERVICE ||
            history[history.length - 1] === FIXED_SCREENS.YCLIENTS_DATETIME,
        staffId: formController.formState.staffId,
        datetime: formController.formState.datetime,
        selectedServices: formController.formState.services,
        setSelectedServices: formController.setServices,
    });
    const datesState = useDates({
        active: history[history.length - 1] === FIXED_SCREENS.YCLIENTS_DATETIME,
        service_ids: formController.formState.services,
        staff_id: formController.formState.staffId,
        datetime: formController.formState.datetime,
        setDateTime: formController.setDateTime,
        resetDateTime: formController.resetDateTime,
    });
    const masterState = useMaster({
        staffId: formController.activeMaster,
        masters: mastersState.masters,
    });
    const activitiesState = useActivities({
        active: history[history.length - 1] === FIXED_SCREENS.YCLIENTS_ACTIVITIES,
    });

    const filialState = useFilialInfo({
        active: history[history.length - 1] === FIXED_SCREENS.YCLIENTS_PLACE,
    });

    const goToMaster = useCallback(
        (id: number) => {
            formController.setActiveMaster(id);
            goToPage(FIXED_SCREENS.YCLIENTS_MASTER_CARD);
        },
        [goToPage, formController],
    );

    const goToActivity = useCallback(
        (id: number) => {
            formController.setActivity(id);
            goToPage(FIXED_SCREENS.YCLIENTS_ACTIVITY);
        },
        [goToPage, formController],
    );

    const goBackWithLogic = useCallback(() => {
        const curPage = history[history.length - 1];
        if (curPage === FIXED_SCREENS.YCLIENTS_SERVICE) {
            formController.resetServices();
            servicesState.resetServices();
        } else if (curPage === FIXED_SCREENS.YCLIENTS_DATETIME) {
            formController.setDateTime('');
        } else if (curPage === FIXED_SCREENS.YCLIENTS_MASTER) {
            formController.setStaffId(null);
            mastersState.resetMasters();
        } else if (curPage === FIXED_SCREENS.YCLIENTS_ACTIVITY) {
            formController.resetActivity();
        }
        goBack();
    }, [history, goBack, formController, servicesState, mastersState]);

    const goToIndividualWithLogic = useCallback(() => {
        if (formData.data.form.sequence_type === 'menu') {
            goToSpecific.goToIndividual();
        } else {
            goToPage(formController.getNextStep(history[history.length - 1]));
        }
    }, [goToPage, formController, formData.data.form.sequence_type, goToSpecific, history]);

    const onSignUpAgain = useCallback(() => {
        goToFirstPage();
        formController.resetDataState();
    }, [formController, goToFirstPage]);

    const onChangePreselectedService = useCallback(() => {
        goToPage(FIXED_SCREENS.YCLIENTS_SERVICE, true);
    }, [goToPage]);

    const quickWriteState = useQuickWrite({
        active: history[history.length - 1] === FIXED_SCREENS.YCLIENTS_QUICK_WRITE,
        setQuickWriteState: formController.setQuickWriteState,
    });

    return (
        <YClientsForm
            activePanelId={history[history.length - 1]}
            isBack={isBack}
            clickToCall={clickToCallAnalyticEvent}
            clickToExternalLink={clickToExternalLinkAnalyticEvent}
            formController={formController}
            goBack={goBackWithLogic}
            navigate={{
                ...goToSpecific,
                goToMaster,
                goToActivity,
                goToIndividual: goToIndividualWithLogic,
                onSignUpAgain,
                onChangePreselectedService,
            }}
            mastersState={mastersState}
            masterState={masterState}
            filialState={filialState}
            servicesState={servicesState}
            datesState={datesState}
            activitiesState={activitiesState}
            quickWriteState={quickWriteState}
            closeForm={closeForm}
            plusActivityRecordsCount={formController.plusActivityRecordsCount}
            minusActivityRecordsCount={formController.minusActivityRecordsCount}
            history={history}
            layout={layout}
            leadForm={formData.data}
            isShowCloseBtn={isShowCloseBtn}
            onTransition={onTransition}
            isBridgeAvailable={!!bridge}
            phoneVerificationController={formController.phoneVerificationController}
            preSelectedServiceID={initialServiceId}
        />
    );
};

const AppYClientsWrapper = (props: AppYClientsWrapperProps) => {
    const [asyncInitData, setAsyncInitData] = useState<{ready: boolean; serviceId: number | null}>({
        ready: false,
        serviceId: null,
    });

    const {serviceId, formDataHash} = useSearchParams();

    const checkAwailableSearchService = useCallback(async () => {
        const {data} = await fetchServices(new URLSearchParams({lead_form_data: formDataHash}).toString());

        const services = data.data.services;

        const hasService = services.some((s) => s.id === serviceId);

        setAsyncInitData({
            ready: true,
            serviceId: hasService ? serviceId : 0,
        });
    }, [serviceId, formDataHash]);

    useEffect(() => {
        setAsyncInitData({
            ready: false,
            serviceId: null,
        });
        // если пришел service_id
        // проверяем доступна ли текущая услуга
        // если не доступна - ничего не меняем в флоу запили
        // если доступна - сетим эту услугу как выбранную и выставляем первым экраном выбор мастера
        if (serviceId) {
            checkAwailableSearchService();
        } else {
            setAsyncInitData({
                ready: true,
                serviceId: null,
            });
        }
    }, [checkAwailableSearchService, serviceId]);

    return asyncInitData.ready ? (
        <AppYClients
            {...props}
            initialServiceId={asyncInitData.serviceId}
        />
    ) : (
        <Spinner />
    );
};

export default AppYClientsWrapper;
